import { createHash } from 'node:crypto';
import type { Stream } from 'node:stream';
import { MastraBase } from '@mastra/core/base';
import { ErrorCategory, ErrorDomain, MastraError } from '@mastra/core/error';
import type { MCPServerBase } from '@mastra/core/mcp';
import type { Tool } from '@mastra/core/tools';
import { DEFAULT_REQUEST_TIMEOUT_MSEC } from '@modelcontextprotocol/client';
import type { ProgressNotification, Prompt, Resource, ResourceTemplateType } from '@modelcontextprotocol/client';
import equal from 'fast-deep-equal';
import { UnauthorizedError } from '../shared/oauth-types';
import { InternalMastraMCPClient } from './client';
import type { MastraMCPServerDefinition, MCPServerAuthState } from './client';
import { getMCPDiscoveryErrorDetails, isReconnectableMCPError } from './error-utils';
import type { MCPDiscoveryErrorDetails } from './error-utils';
import { createOAuthCallbackServer } from './oauth-callback-server';
import type { OAuthCallbackServer } from './oauth-callback-server';
import { MCPOAuthClientProvider } from './oauth-provider';
import { MCPClientServerProxy } from './server-proxy';
import type {
  MCPClientInfo,
  MCPServerImplementation,
  SerializableMCPToolCatalog,
  SerializableMCPToolDefinition,
} from './types';

const mcpClientInstances = new Map<string, InstanceType<typeof MCPClient>>();
const TOOL_DISCOVERY_MAX_ATTEMPTS = 2;

// Outcome of a single server's discovery within discoverAcrossServers(). An
// explicit discriminated union (rather than `{ value?: T; error?: string }`) so
// that narrowing on `error` also narrows `value` — Promise.all would otherwise
// widen the two branches into independent optional props and break the fold.
type ServerDiscoveryResult<T> =
  | { serverName: string; value: T; error: undefined; duration: number }
  | { serverName: string; value: undefined; error: MCPDiscoveryErrorDetails; duration: number };

/** Options for aggregate discovery across configured MCP servers. */
export interface MCPDiscoveryOptions {
  /** Maximum time to wait for each server's discovery operation, in milliseconds. */
  perServerTimeoutMs?: number;
}

// Matches the entire 127.0.0.0/8 range in dotted-quad form. `URL` normalizes
// IPv4 hosts to four octets (so `127.1` becomes `127.0.0.1`), so anchoring the
// pattern is enough — and it rejects lookalikes like `127.evil.com` that a
// prefix check would wrongly accept and leak the authorization code to.
const LOOPBACK_IPV4 = /^127\.(?:\d{1,3})\.(?:\d{1,3})\.(?:\d{1,3})$/;

// Whether a hostname is a loopback address authenticate() accepts for the
// provider's redirect URL (RFC 8252 loopback redirection). Kept in sync with the
// mastracode config parser, which accepts any 127.0.0.0/8 host, so a config that
// parses (e.g. 127.0.0.2) does not later fail here.
function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '[::1]' || hostname === '::1' || LOOPBACK_IPV4.test(hostname);
}

/**
 * Configuration options for creating an MCPClient instance.
 */
export interface MCPClientOptions {
  /** Optional unique identifier to prevent memory leaks when creating multiple instances with identical configurations */
  id?: string;
  /** Map of server names to their connection configurations (stdio or HTTP-based) */
  servers: Record<string, MastraMCPServerDefinition>;
  /** Optional global timeout in milliseconds for all servers (default: 60000ms) */
  timeout?: number;
  /**
   * Default `clientInfo` every server receives on connect and with each request.
   * Per-server `clientInfo` overrides it field-wise. Defaults to the server key and `'1.0.0'`.
   */
  clientInfo?: MCPClientInfo;
}

/**
 * Manages Model Context Protocol (MCP) server connections and tool namespacing,
 * with access to tools, resources, prompts, and elicitation.
 *
 * Configure authentication when required by your servers.
 *
 * @example
 * `yourServerUrl` is a URL for a running MCP server's HTTP endpoint.
 * ```typescript
 * import { MCPClient } from '@mastra/mcp';
 *
 * const mcp = new MCPClient({
 *   servers: {
 *     remote: { url: yourServerUrl },
 *   },
 * });
 * ```
 *
 * @see For documentation bundled with your installed package, locate
 * `@mastra/mcp/package.json` with your project's resolver or package-manager
 * tooling, then read `dist/docs/SKILL.md` from that package root and follow its
 * reference links. Use package-manager tools for virtual or archived packages.
 *
 * @see [MCP client documentation](https://mastra.ai/reference/tools/mcp-client)
 * if packaged docs are unavailable.
 */
export class MCPClient extends MastraBase {
  private serverConfigs: Record<string, MastraMCPServerDefinition> = {};
  private id: string;
  private defaultTimeout: number;
  private defaultClientInfo?: MCPClientInfo;
  private mcpClientsById = new Map<string, InternalMastraMCPClient>();
  private disconnectPromise: Promise<void> | null = null;
  private authFlowsByServer = new Map<string, Promise<void>>();
  private authCallbackServersByServer = new Map<string, OAuthCallbackServer>();
  /**
   * Per-server abort controllers for in-flight authorization flows. Created
   * synchronously at the start of {@link runAuthorizationFlow} — before the
   * callback server exists — so cancel/disconnect can interrupt the setup phase
   * (discovery, registration, port binding) and not just the waitForCode wait.
   */
  private authAbortControllersByServer = new Map<string, AbortController>();

  /**
   * Creates a new MCPClient instance for managing MCP server connections.
   *
   * The client automatically manages connection lifecycle and prevents memory leaks by
   * caching instances with identical configurations.
   *
   * @param args - Configuration options
   * @param args.id - Optional unique identifier to allow multiple instances with same config
   * @param args.servers - Map of server names to server configurations
   * @param args.timeout - Optional global timeout in milliseconds (default: 60000)
   *
   * @throws {Error} If multiple instances with identical config are created without an ID
   *
   * @example
   * ```typescript
   * const mcp = new MCPClient({
   *   servers: {
   *     weatherServer: {
   *       url: new URL('http://localhost:8080/mcp'),
   *       requestInit: {
   *         headers: { Authorization: 'Bearer token' }
   *       }
   *     }
   *   },
   *   timeout: 30000
   * });
   * ```
   */
  constructor(args: MCPClientOptions) {
    super({ name: 'MCPClient' });
    this.defaultTimeout = args.timeout ?? DEFAULT_REQUEST_TIMEOUT_MSEC;
    this.defaultClientInfo = args.clientInfo && { ...args.clientInfo };
    this.serverConfigs = args.servers;
    this.id = args.id ?? this.makeId();

    if (args.id) {
      this.id = args.id;
      const cached = mcpClientInstances.get(this.id);

      if (cached && (!equal(cached.serverConfigs, args.servers) || !equal(cached.defaultClientInfo, args.clientInfo))) {
        const existingInstance = mcpClientInstances.get(this.id);
        if (existingInstance) {
          void existingInstance.disconnect();
          mcpClientInstances.delete(this.id);
        }
      }
    } else {
      this.id = this.makeId();
    }

    // to prevent memory leaks return the same MCP server instance when configured the same way multiple times
    const existingInstance = mcpClientInstances.get(this.id);
    if (existingInstance) {
      if (!args.id) {
        throw new Error(`MCPClient was initialized multiple times with the same configuration options.

This error is intended to prevent memory leaks.

To fix this you have three different options:
1. If you need multiple MCPClient class instances with identical server configurations, set an id when configuring: new MCPClient({ id: "my-unique-id" })
2. Call "await client.disconnect()" after you're done using the client and before you recreate another instance with the same options. If the identical MCPClient instance is already closed at the time of re-creating it, you will not see this error.
3. If you only need one instance of MCPClient in your app, refactor your code so it's only created one time (ex. move it out of a loop into a higher scope code block)
`);
      }
      return existingInstance;
    }

    mcpClientInstances.set(this.id, this);
    this.addToInstanceCache();
    return this;
  }

  /**
   * Provides access to progress-related operations for tracking long-running operations.
   *
   * Progress tracking allows MCP servers to send updates about the status of ongoing operations,
   * providing real-time feedback to users about task completion and current state.
   *
   * @example
   * ```typescript
   * // Set up handler for progress updates from a server
   * await mcp.progress.onUpdate('serverName', (params) => {
   *   console.log(`Progress: ${params.progress}%`);
   *   console.log(`Status: ${params.message}`);
   *
   *   if (params.total) {
   *     console.log(`Completed ${params.progress} of ${params.total} items`);
   *   }
   * });
   * ```
   */
  public get progress() {
    this.addToInstanceCache();
    return {
      onUpdate: async (serverName: string, handler: (params: ProgressNotification['params']) => void) => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          return internalClient.progress.onUpdate(handler);
        } catch (err) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_ON_UPDATE_PROGRESS_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: {
                serverName,
              },
            },
            err,
          );
        }
      },
    };
  }

  /**
   * Provides access to resource-related operations across all configured servers.
   *
   * Resources represent data exposed by MCP servers (files, database records, API responses, etc.).
   *
   * @example
   * ```typescript
   * // List all resources from all servers
   * const allResources = await mcp.resources.list();
   * Object.entries(allResources).forEach(([serverName, resources]) => {
   *   console.log(`${serverName}: ${resources.length} resources`);
   * });
   *
   * // Read a specific resource
   * const content = await mcp.resources.read('weatherServer', 'file://data.json');
   *
   * // Receive resource updates
   * await mcp.resources.onUpdated('weatherServer', async (params) => {
   *   console.log(`Resource updated: ${params.uri}`);
   * });
   * await mcp.resources.subscribe('weatherServer', 'file://data.json');
   * ```
   */
  public get resources() {
    this.addToInstanceCache();
    return {
      /**
       * Lists all available resources from all configured servers.
       *
       * Returns a map of server names to their resource arrays. Errors for individual
       * servers are logged but don't throw - failed servers return empty arrays.
       *
       * @returns Promise resolving to object mapping server names to resource arrays
       *
       * @example
       * ```typescript
       * const resources = await mcp.resources.list();
       * console.log(resources.weatherServer); // Array of resources
       * ```
       */
      list: async (): Promise<Record<string, Resource[]>> => (await this.listResourcesWithErrors()).resources,
      /**
       * Lists resources while preserving per-server discovery failures.
       * The existing `list()` method remains the success-only convenience API.
       */
      listWithErrors: (options?: MCPDiscoveryOptions) => this.listResourcesWithErrors(options),
      /**
       * Lists all available resource templates from all configured servers.
       *
       * Resource templates are URI templates (RFC 6570) describing dynamic resources.
       * Errors for individual servers are logged but don't throw.
       *
       * @returns Promise resolving to object mapping server names to template arrays
       *
       * @example
       * ```typescript
       * const templates = await mcp.resources.templates();
       * console.log(templates.weatherServer); // Array of resource templates
       * ```
       */
      templates: async (): Promise<Record<string, ResourceTemplateType[]>> =>
        (await this.listResourceTemplatesWithErrors()).templates,
      /**
       * Lists resource templates while preserving per-server discovery failures.
       * The existing `templates()` method remains the success-only convenience API.
       */
      templatesWithErrors: (options?: MCPDiscoveryOptions) => this.listResourceTemplatesWithErrors(options),
      /**
       * Reads the content of a specific resource from a server.
       *
       * @param serverName - Name of the server to read from
       * @param uri - URI of the resource to read
       * @returns Promise resolving to the resource content
       * @throws {MastraError} If reading the resource fails
       *
       * @example
       * ```typescript
       * const content = await mcp.resources.read('weatherServer', 'file://config.json');
       * console.log(content.contents[0].text);
       * ```
       */
      read: async (serverName: string, uri: string) => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          return internalClient.resources.read(uri);
        } catch (error) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_READ_RESOURCE_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: {
                serverName,
                uri,
              },
            },
            error,
          );
        }
      },
      /**
       * Subscribes to update notifications for a resource on a server. Subscriptions ride
       * the client's single `subscriptions/listen` stream and are restored after reconnects.
       *
       * @param serverName - Name of the server
       * @param uri - URI of the resource to watch
       * @throws {MastraError} If the server declines the subscription
       *
       * @example
       * ```typescript
       * await mcp.resources.onUpdated('weatherServer', ({ uri }) => console.log(`updated ${uri}`));
       * await mcp.resources.subscribe('weatherServer', 'file://data.json');
       * ```
       */
      subscribe: async (serverName: string, uri: string): Promise<void> => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          await internalClient.resources.subscribe(uri);
        } catch (err) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_SUBSCRIBE_RESOURCE_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: { serverName, uri },
            },
            err,
          );
        }
      },
      /**
       * Stops update notifications for a resource previously passed to `subscribe`.
       *
       * @param serverName - Name of the server
       * @param uri - URI of the resource
       * @throws {MastraError} If the stream cannot be updated
       */
      unsubscribe: async (serverName: string, uri: string): Promise<void> => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          await internalClient.resources.unsubscribe(uri);
        } catch (err) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_UNSUBSCRIBE_RESOURCE_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: { serverName, uri },
            },
            err,
          );
        }
      },
      /**
       * Sets a notification handler for when subscribed resources are updated on a server.
       * Updates arrive for resources passed to `subscribe`.
       *
       * @param serverName - Name of the server to monitor
       * @param handler - Callback function receiving the updated resource URI
       * @returns Promise resolving when handler is registered
       * @throws {MastraError} If setting up the handler fails
       *
       * @example
       * ```typescript
       * await mcp.resources.onUpdated('weatherServer', async (params) => {
       *   console.log(`Resource updated: ${params.uri}`);
       *   const content = await mcp.resources.read('weatherServer', params.uri);
       * });
       * ```
       */
      onUpdated: async (serverName: string, handler: (params: { uri: string }) => void) => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          return internalClient.resources.onUpdated(handler);
        } catch (err) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_ON_UPDATED_RESOURCE_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: {
                serverName,
              },
            },
            err,
          );
        }
      },
      /**
       * Sets a notification handler for when the resource list changes on a server.
       *
       * @param serverName - Name of the server to monitor
       * @param handler - Callback function invoked when resources are added/removed
       * @returns Promise resolving when handler is registered
       * @throws {MastraError} If setting up the handler fails
       *
       * @example
       * ```typescript
       * await mcp.resources.onListChanged('weatherServer', async () => {
       *   console.log('Resource list changed, re-fetching...');
       *   const resources = await mcp.resources.list();
       * });
       * ```
       */
      onListChanged: async (serverName: string, handler: () => void) => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          return internalClient.resources.onListChanged(handler);
        } catch (err) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_ON_LIST_CHANGED_RESOURCE_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: {
                serverName,
              },
            },
            err,
          );
        }
      },
    };
  }

  /**
   * Provides access to prompt-related operations across all configured servers.
   *
   * Prompts are reusable message templates exposed by MCP servers that can be parameterized
   * and used for AI interactions.
   *
   * @example
   * ```typescript
   * // List all prompts from all servers
   * const allPrompts = await mcp.prompts.list();
   * Object.entries(allPrompts).forEach(([serverName, prompts]) => {
   *   console.log(`${serverName}: ${prompts.map(p => p.name).join(', ')}`);
   * });
   *
   * // Get a specific prompt with arguments
   * const prompt = await mcp.prompts.get({
   *   serverName: 'weatherServer',
   *   name: 'forecast-template',
   *   args: { city: 'London', days: 7 }
   * });
   * ```
   */
  public get prompts() {
    this.addToInstanceCache();
    return {
      /**
       * Lists all available prompts from all configured servers.
       *
       * Returns a map of server names to their prompt arrays. Errors for individual
       * servers are logged but don't throw - failed servers return empty arrays.
       *
       * @returns Promise resolving to object mapping server names to prompt arrays
       *
       * @example
       * ```typescript
       * const prompts = await mcp.prompts.list();
       * console.log(prompts.weatherServer); // Array of prompts
       * ```
       */
      list: async (): Promise<Record<string, Prompt[]>> => (await this.listPromptsWithErrors()).prompts,
      /**
       * Lists prompts while preserving per-server discovery failures.
       * The existing `list()` method remains the success-only convenience API.
       */
      listWithErrors: (options?: MCPDiscoveryOptions) => this.listPromptsWithErrors(options),
      /**
       * Retrieves a specific prompt with its messages from a server.
       *
       * @param params - Parameters for the prompt request
       * @param params.serverName - Name of the server to retrieve from
       * @param params.name - Name of the prompt to retrieve
       * @param params.args - Optional arguments to populate the prompt template
       * @returns Promise resolving to the prompt result with messages
       * @throws {MastraError} If fetching the prompt fails
       *
       * @example
       * ```typescript
       * const prompt = await mcp.prompts.get({
       *   serverName: 'weatherServer',
       *   name: 'forecast',
       *   args: { city: 'London' },
       * });
       * console.log(prompt.messages);
       * ```
       */
      get: async ({ serverName, name, args }: { serverName: string; name: string; args?: Record<string, any> }) => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          return internalClient.prompts.get({ name, args });
        } catch (error) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_GET_PROMPT_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: {
                serverName,
                name,
              },
            },
            error,
          );
        }
      },
      /**
       * Sets a notification handler for when the prompt list changes on a server.
       *
       * @param serverName - Name of the server to monitor
       * @param handler - Callback function invoked when prompts are added/removed/modified
       * @returns Promise resolving when handler is registered
       * @throws {MastraError} If setting up the handler fails
       *
       * @example
       * ```typescript
       * await mcp.prompts.onListChanged('weatherServer', async () => {
       *   console.log('Prompt list changed, re-fetching...');
       *   const prompts = await mcp.prompts.list();
       * });
       * ```
       */
      onListChanged: async (serverName: string, handler: () => void) => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          return internalClient.prompts.onListChanged(handler);
        } catch (error) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_ON_LIST_CHANGED_PROMPT_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: {
                serverName,
              },
            },
            error,
          );
        }
      },
    };
  }

  /**
   * Provides access to tool-related notification operations across all configured servers.
   *
   * To fetch tools, use `listTools()` or `listToolsets()`.
   *
   * @example
   * ```typescript
   * // React to tool list changes on a server
   * await mcp.tools.onListChanged('weatherServer', async () => {
   *   console.log('Tool list changed, re-fetching...');
   *   const tools = await mcp.listTools();
   * });
   * ```
   */
  public get tools() {
    this.addToInstanceCache();
    return {
      /**
       * Sets a notification handler for when the tool list changes on a server.
       *
       * @param serverName - Name of the server to monitor
       * @param handler - Callback function invoked when tools are added/removed/modified
       * @returns Promise resolving when handler is registered
       * @throws {MastraError} If setting up the handler fails
       *
       * @example
       * ```typescript
       * await mcp.tools.onListChanged('weatherServer', async () => {
       *   const tools = await mcp.listTools();
       * });
       * ```
       */
      onListChanged: async (serverName: string, handler: () => void) => {
        try {
          const internalClient = await this.getConnectedClientForServer(serverName);
          await internalClient.setToolListChangedNotificationHandler(handler);
        } catch (error) {
          throw new MastraError(
            {
              id: 'MCP_CLIENT_ON_LIST_CHANGED_TOOLS_FAILED',
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              details: {
                serverName,
              },
            },
            error,
          );
        }
      },
    };
  }

  private addToInstanceCache() {
    if (!mcpClientInstances.has(this.id)) {
      mcpClientInstances.set(this.id, this);
    }
  }

  private makeId() {
    const text = JSON.stringify(
      this.defaultClientInfo === undefined
        ? this.serverConfigs
        : { servers: this.serverConfigs, clientInfo: this.defaultClientInfo },
    ).normalize('NFKC');
    return createHash('sha256').update('MCPClient').update(text).digest('hex');
  }

  /**
   * Disconnects from all MCP servers and cleans up resources.
   *
   * This method gracefully closes all server connections and clears internal caches.
   * Safe to call multiple times - subsequent calls will wait for the first disconnect to complete.
   *
   * @example
   * ```typescript
   * // Cleanup on application shutdown
   * process.on('SIGTERM', async () => {
   *   await mcp.disconnect();
   *   process.exit(0);
   * });
   * ```
   */
  public async disconnect() {
    // Helps to prevent race condition
    // If there is already a disconnect ongoing, return the existing promise.
    if (this.disconnectPromise) {
      return this.disconnectPromise;
    }

    this.disconnectPromise = (async () => {
      try {
        if (mcpClientInstances.get(this.id) === this) {
          mcpClientInstances.delete(this.id);
        }

        // Tear down any in-flight authorization: each callback server owns a live
        // loopback HTTP port, and closing it rejects the flow's waitForCode. Await
        // the flow settlements so a disconnect during authentication does not leave
        // a bound port or a dangling promise keeping the process alive.
        const pendingFlows = Array.from(this.authFlowsByServer.values());
        // Abort first so flows still in their setup phase (no callback server
        // bound yet) unblock instead of parking on waitForCode and deadlocking
        // the awaited settlement below.
        for (const controller of this.authAbortControllersByServer.values()) {
          controller.abort();
        }
        await Promise.allSettled(Array.from(this.authCallbackServersByServer.values()).map(server => server.close()));
        await Promise.allSettled(pendingFlows);
        this.authAbortControllersByServer.clear();
        this.authCallbackServersByServer.clear();
        this.authFlowsByServer.clear();

        // Disconnect all clients in the cache
        await Promise.allSettled(Array.from(this.mcpClientsById.values()).map(client => client.disconnect()));
        this.mcpClientsById.clear();
      } finally {
        this.disconnectPromise = null;
      }
    })();

    return this.disconnectPromise;
  }

  /**
   * Reconnects a single MCP server by name.
   *
   * If the server is already connected, it will be forcefully disconnected and reconnected.
   * If the server has never been connected, a new connection will be established.
   *
   * @param serverName - The name of the server to reconnect (must match a key in `servers`)
   * @throws {Error} If the server name is not found in the configuration
   *
   * @example
   * ```typescript
   * // Reconnect a specific server after it fails
   * await mcp.reconnectServer('weatherServer');
   * ```
   */
  public async reconnectServer(serverName: string): Promise<void> {
    const existingClient = this.mcpClientsById.get(serverName);
    if (existingClient) {
      await existingClient.forceReconnect();
    } else {
      await this.getConnectedClientForServer(serverName);
    }
  }

  /**
   * Runs the interactive OAuth authorization-code flow for a server.
   *
   * Requires the server to be configured with an MCPOAuthClientProvider whose
   * redirect URL points at a loopback address. The flow:
   *
   * 1. Starts a loopback callback server on the redirect URL's port (falling
   *    back to the next sequential ports when it is in use)
   * 2. Attempts a connection so the SDK runs authorization server discovery
   *    (identifying the client by its pre-registered id or its client metadata
   *    URL) and delivers the authorization URL through the provider's
   *    `onRedirectToAuthorization` callback — the host directs the user there
   * 3. Waits for the browser to deliver the authorization code, validates the
   *    OAuth state, exchanges the code for tokens, and reconnects
   *
   * Concurrent calls for the same server join the pending flow (the joiner's
   * options are ignored — the pending flow keeps its own timeout); different
   * servers authenticate independently. Each server needs its own provider
   * instance: the flow pins session state on the provider, so sharing one
   * MCPOAuthClientProvider across servers is not supported. Hosts with custom
   * redirect handling (e.g. a web app with an HTTPS redirect URL) should
   * drive MCPOAuthClientProvider directly instead.
   *
   * @param serverName - The name of the server to authenticate (must match a key in `servers`)
   * @param options.timeoutMs - How long to wait for the browser callback (default 5 minutes)
   * @throws {Error} If the server has no MCPOAuthClientProvider or its redirect URL is not loopback
   *
   * @example
   * ```typescript
   * if (mcp.getServerAuthState('weatherServer') === 'needs-auth') {
   *   await mcp.authenticate('weatherServer');
   * }
   * ```
   */
  public async authenticate(serverName: string, options?: { timeoutMs?: number }): Promise<void> {
    // Wait for an in-flight disconnect to finish before starting. disconnect()
    // snapshots and then clears the auth-flow maps, so a flow registered during
    // that window would have its abort controller and callback server wiped
    // without being closed, orphaning the loopback port. Swallow a rejected
    // disconnect: it must not surface as an authenticate() failure.
    if (this.disconnectPromise) {
      await this.disconnectPromise.catch(() => {});
    }

    const pendingFlow = this.authFlowsByServer.get(serverName);
    if (pendingFlow) {
      return pendingFlow;
    }

    // Correctness contract: nothing between here and runAuthorizationFlow's
    // synchronous abort-controller registration may await. The disconnect-race
    // guard above relies on this flow's map entries landing in the same tick, so
    // that any disconnect() starting afterwards snapshots and tears them down.
    const flow = this.runAuthorizationFlow(serverName, options).finally(() => {
      this.authFlowsByServer.delete(serverName);
    });
    this.authFlowsByServer.set(serverName, flow);
    return flow;
  }

  /**
   * OAuth authorization state of a configured server.
   *
   * Returns `undefined` for servers without an authProvider and for servers
   * that have not attempted a connection yet.
   */
  public getServerAuthState(serverName: string): MCPServerAuthState | undefined {
    return this.mcpClientsById.get(serverName)?.authState;
  }

  private async runAuthorizationFlow(serverName: string, options?: { timeoutMs?: number }): Promise<void> {
    // Register the abort controller synchronously, before the first await, so a
    // cancel/disconnect during the setup phase (discovery, registration, port
    // binding) can interrupt the flow rather than letting it park on waitForCode.
    const abortController = new AbortController();
    this.authAbortControllersByServer.set(serverName, abortController);
    const throwIfAborted = () => {
      if (abortController.signal.aborted) {
        throw new Error(`Authentication for MCP server ${serverName} was cancelled.`);
      }
    };

    // Resources acquired during setup that must be released on every exit path.
    // Tracked here so the single outer finally can tear them down even if a
    // fallible setup step (session begin, port binding) throws.
    let provider: MCPOAuthClientProvider | undefined;
    let sessionStarted = false;
    let callbackServer: OAuthCallbackServer | undefined;

    // Installed before the first fallible step so the abort-controller entry,
    // provider session, and callback server never leak on an early throw.
    try {
      const config = this.getServerConfig(serverName);
      const candidateProvider = config.authProvider;
      if (!(candidateProvider instanceof MCPOAuthClientProvider)) {
        throw new Error(
          `Cannot authenticate MCP server ${serverName}: it is not configured with an MCPOAuthClientProvider.`,
        );
      }
      provider = candidateProvider;

      const redirectUrl = new URL(provider.redirectUrl.toString());
      if (redirectUrl.protocol !== 'http:' || !isLoopbackHostname(redirectUrl.hostname)) {
        throw new Error(
          `Cannot authenticate MCP server ${serverName}: the provider's redirect URL must be a loopback address, got ${redirectUrl.origin}.`,
        );
      }

      const state = await provider.beginAuthorizationSession();
      sessionStarted = true;
      // A cancel that arrived during beginAuthorizationSession() has no callback
      // server to close yet, so bail here before binding a port and parking.
      throwIfAborted();

      callbackServer = await createOAuthCallbackServer({ redirectUrl, state });
      // A cancel during port binding: bail before we ever wait for a code that
      // will never arrive. The outer finally closes the freshly-bound server.
      throwIfAborted();
      this.authCallbackServersByServer.set(serverName, callbackServer);

      // Point the authorization request at the callback URL that actually bound.
      provider.applyResolvedRedirectUrl(callbackServer.url);

      const client = await this.getClientForServer(serverName);
      try {
        // With valid stored tokens this simply connects; otherwise the SDK
        // delivers the authorization URL and throws UnauthorizedError.
        await client.connect();
        return;
      } catch (error) {
        if (!(error instanceof UnauthorizedError)) {
          throw this.handleConnectError(serverName, error);
        }
      }

      const { code, iss } = await callbackServer.waitForCode(options);
      await client.finishAuth(code, iss);

      try {
        await client.connect();
      } catch (error) {
        throw error instanceof UnauthorizedError ? error : this.handleConnectError(serverName, error);
      }
    } finally {
      this.authCallbackServersByServer.delete(serverName);
      this.authAbortControllersByServer.delete(serverName);
      if (sessionStarted) {
        provider?.endAuthorizationSession();
      }
      await callbackServer?.close();
    }
  }

  /**
   * Cancels a pending {@link authenticate} flow for a server.
   *
   * Tears down the loopback callback server immediately — the pending
   * authenticate() call rejects. Useful when the user closed the browser
   * without completing consent, which the host cannot observe.
   *
   * The resulting auth state depends on how far the flow had progressed: a flow
   * cancelled after the server rejected the connection with a 401 stays in the
   * `needs-auth` state so it can be retried right away, while a flow cancelled
   * during the setup phase (before any connection was attempted) leaves the
   * state unchanged — typically `undefined`.
   *
   * @param serverName - The name of the server whose flow to cancel
   * @returns `true` if a pending flow was cancelled, `false` when no flow was pending
   */
  public async cancelAuthentication(serverName: string): Promise<boolean> {
    const pendingFlow = this.authFlowsByServer.get(serverName);
    if (!pendingFlow) {
      return false;
    }

    // Abort first so a flow still in its setup phase (no callback server bound
    // yet) is interrupted before it can park on waitForCode; then close the
    // callback server if one exists, which rejects an in-progress waitForCode.
    this.authAbortControllersByServer.get(serverName)?.abort();
    await this.authCallbackServersByServer.get(serverName)?.close();
    await pendingFlow.catch(() => undefined);
    return true;
  }

  /**
   * Returns instructions advertised by connected MCP servers during discovery.
   *
   * Servers that have not connected yet, or did not advertise instructions,
   * return `undefined`.
   */
  public getServerInstructions(): Record<string, string | undefined> {
    const instructions: Record<string, string | undefined> = {};

    for (const serverName of Object.keys(this.serverConfigs)) {
      instructions[serverName] = this.mcpClientsById.get(serverName)?.instructions;
    }

    return instructions;
  }

  /**
   * Returns the identity each connected MCP server announced when the
   * connection was established (`serverInfo`: name, version, and optionally
   * title, description, websiteUrl, and icons), keyed by server name.
   *
   * A server's entry is `undefined` if it has not connected yet, or if it
   * connected over the 2026-07-28 revision without announcing an identity, so
   * don't use this to check connection state. Icon and website URLs come from
   * the remote server and are not validated.
   */
  public getServerInfo(): Record<string, MCPServerImplementation | undefined> {
    const info: Record<string, MCPServerImplementation | undefined> = {};
    for (const serverName of Object.keys(this.serverConfigs)) {
      info[serverName] = this.mcpClientsById.get(serverName)?.serverInfo;
    }
    return info;
  }

  /**
   * The protocol revision negotiated with each configured server, keyed by
   * server name. Servers that have not connected yet return `undefined`.
   */
  public getServerProtocolVersions(): Record<string, string | undefined> {
    const versions: Record<string, string | undefined> = {};
    for (const serverName of Object.keys(this.serverConfigs)) {
      versions[serverName] = this.mcpClientsById.get(serverName)?.negotiatedProtocolVersion;
    }
    return versions;
  }

  /**
   * Retrieves all tools from all configured servers with namespaced names.
   *
   * Tool names are namespaced as `serverName_toolName` to prevent conflicts between servers.
   * This method is intended to be passed directly to an Agent definition.
   *
   * @returns Object mapping namespaced tool names to tool implementations.
   * Errors for individual servers are logged but don't throw - failed servers are skipped.
   * Transient connection failures are retried once after reconnecting the affected server.
   *
   * @example
   * ```typescript
   * const agent = new Agent({
   *   id: 'multi-tool-agent',
   *   name: 'Multi-tool Agent',
   *   instructions: 'You have access to weather and stock tools.',
   *   model: 'openai/gpt-4',
   *   tools: await mcp.listTools(), // weather_getWeather, stockPrice_getPrice
   * });
   * ```
   */
  public async listTools(): Promise<Record<string, Tool<any, any, any, any>>> {
    const result = await this.listToolsWithErrors();
    return result.tools;
  }

  /**
   * Retrieves all tools from all configured servers with namespaced names,
   * along with any per-server errors.
   *
   * Like listTools(), but also returns errors for servers that failed to connect
   * or list tools. This allows callers to report specific failure reasons per server.
   *
   * @returns Object with successful `tools`, legacy string `errors`, and structured `errorDetails`.
   * Transient connection failures are retried once after reconnecting the affected server.
   *
   * @example
   * ```typescript
   * const { tools, errors, errorDetails } = await mcp.listToolsWithErrors();
   * for (const [name, err] of Object.entries(errors)) {
   *   console.error(`Server ${name} failed: ${err}`, errorDetails[name]);
   * }
   * ```
   */
  public async listToolsWithErrors(options?: MCPDiscoveryOptions): Promise<{
    tools: Record<string, Tool<any, any, any, any>>;
    errors: Record<string, string>;
    errorDetails: Record<string, MCPDiscoveryErrorDetails>;
    durations?: Record<string, number>;
  }> {
    this.addToInstanceCache();
    const connectedTools: Record<string, Tool<any, any, any, any>> = {};
    const errors: Record<string, string> = {};
    const errorDetails: Record<string, MCPDiscoveryErrorDetails> = {};
    const durations: Record<string, number> = {};

    const settled = await this.discoverAcrossServers(
      serverName => this.getToolsForServer(serverName),
      {
        errorId: 'MCP_CLIENT_GET_TOOLS_FAILED',
        logMessage: 'Failed to list tools from server:',
      },
      options,
    );

    for (const { serverName, value, error, duration } of settled) {
      durations[serverName] = duration;
      if (error !== undefined) {
        errors[serverName] = error.message;
        errorDetails[serverName] = error;
        continue;
      }
      for (const [toolName, toolConfig] of Object.entries(value)) {
        connectedTools[`${serverName}_${toolName}`] = toolConfig;
      }
    }

    const result = { tools: connectedTools, errors, errorDetails };
    return options ? { ...result, durations } : result;
  }

  /**
   * Returns toolsets organized by server name for dynamic tool injection.
   *
   * Unlike listTools(), this returns tools grouped by server without namespacing.
   * This is intended to be passed dynamically to the generate() or stream() method.
   *
   * @returns Object mapping server names to their tool collections.
   * Errors for individual servers are logged but don't throw - failed servers are skipped.
   *
   * @example
   * ```typescript
   * const agent = new Agent({
   *   id: 'dynamic-agent',
   *   name: 'Dynamic Agent',
   *   instructions: 'You can use tools dynamically.',
   *   model: 'openai/gpt-4',
   * });
   *
   * const response = await agent.stream(prompt, {
   *   toolsets: await mcp.listToolsets(), // { weather: {...}, stockPrice: {...} }
   * });
   * ```
   */
  public async listToolsets(): Promise<Record<string, Record<string, Tool<any, any, any, any>>>> {
    const result = await this.listToolsetsWithErrors();
    return result.toolsets;
  }

  /**
   * Returns toolsets organized by server name, along with any per-server errors.
   *
   * Like listToolsets(), but also returns errors for servers that failed to connect
   * or list tools. This allows callers to report specific failure reasons per server.
   *
   * @returns Object with successful `toolsets`, legacy string `errors`, and structured `errorDetails`.
   * Transient connection failures are retried once after reconnecting the affected server.
   *
   * @example
   * ```typescript
   * const { toolsets, errors, errorDetails } = await mcp.listToolsetsWithErrors();
   * for (const [name, err] of Object.entries(errors)) {
   *   console.error(`Server ${name} failed: ${err}`, errorDetails[name]);
   * }
   * ```
   */
  public async listToolsetsWithErrors(options?: MCPDiscoveryOptions): Promise<{
    toolsets: Record<string, Record<string, Tool<any, any, any, any>>>;
    errors: Record<string, string>;
    errorDetails: Record<string, MCPDiscoveryErrorDetails>;
    durations?: Record<string, number>;
  }> {
    this.addToInstanceCache();
    const connectedToolsets: Record<string, Record<string, Tool<any, any, any, any>>> = {};
    const errors: Record<string, string> = {};
    const errorDetails: Record<string, MCPDiscoveryErrorDetails> = {};
    const durations: Record<string, number> = {};

    const settled = await this.discoverAcrossServers(
      serverName => this.getToolsForServer(serverName),
      {
        errorId: 'MCP_CLIENT_GET_TOOLSETS_FAILED',
        logMessage: 'Failed to list toolsets from server:',
      },
      options,
    );

    for (const { serverName, value, error, duration } of settled) {
      durations[serverName] = duration;
      if (error !== undefined) {
        errors[serverName] = error.message;
        errorDetails[serverName] = error;
        continue;
      }
      connectedToolsets[serverName] = value;
    }

    const result = { toolsets: connectedToolsets, errors, errorDetails };
    return options ? { ...result, durations } : result;
  }

  /**
   * Discovers every configured server's tools as plain, serializable definitions.
   *
   * Unlike `listTools()`/`listToolsets()`, the result contains no functions or references to a
   * live client, so it can be JSON-serialized and cached (Redis, a database, a build artifact)
   * and reused by other processes. Rebuild an executable tool from a cached definition with
   * {@link toolFromDefinition}, which does not reconnect.
   *
   * Definitions are grouped by server and keyed by the server's own tool name, without the
   * `serverName_toolName` namespacing that `listTools()` applies.
   *
   * @example
   * ```typescript
   * // Once, at build time or on the first worker:
   * const definitions = await mcp.listToolDefinitions();
   * await cache.set('mcp-tools', JSON.stringify(definitions));
   *
   * // In every other worker, with no MCP connections opened:
   * const definitions = JSON.parse(await cache.get('mcp-tools'));
   * const tool = mcp.toolFromDefinition('weather', definitions.weather.getForecast);
   * ```
   */
  public async listToolDefinitions(): Promise<SerializableMCPToolCatalog> {
    const result = await this.listToolDefinitionsWithErrors();
    return result.definitions;
  }

  /**
   * Like {@link listToolDefinitions}, but also returns errors for servers that failed to
   * connect instead of surfacing only the servers that succeeded.
   *
   * Useful when caching a catalog, since it lets you avoid persisting a partial manifest that
   * silently omits a server which happened to be down at discovery time.
   * `errors` remains a string map for compatibility; `errorDetails` preserves
   * machine-readable transport status and error codes when available.
   */
  public async listToolDefinitionsWithErrors(options?: MCPDiscoveryOptions): Promise<{
    definitions: SerializableMCPToolCatalog;
    errors: Record<string, string>;
    errorDetails: Record<string, MCPDiscoveryErrorDetails>;
    durations?: Record<string, number>;
  }> {
    this.addToInstanceCache();
    const definitions: SerializableMCPToolCatalog = {};
    const errors: Record<string, string> = {};
    const errorDetails: Record<string, MCPDiscoveryErrorDetails> = {};
    const durations: Record<string, number> = {};

    const settled = await this.discoverAcrossServers(
      async serverName => {
        const client = await this.getConnectedClientForServer(serverName);
        return client.toolDefinitions();
      },
      {
        errorId: 'MCP_CLIENT_GET_TOOL_DEFINITIONS_FAILED',
        logMessage: 'Failed to list tool definitions from server:',
      },
      options,
    );

    for (const { serverName, value, error, duration } of settled) {
      durations[serverName] = duration;
      if (error !== undefined) {
        errors[serverName] = error.message;
        errorDetails[serverName] = error;
        continue;
      }
      definitions[serverName] = value;
    }

    const result = { definitions, errors, errorDetails };
    return options ? { ...result, durations } : result;
  }

  /**
   * Rebuilds an executable Mastra tool from a cached {@link SerializableMCPToolDefinition}.
   *
   * No MCP connection is opened here — that is the point of the method. The underlying client
   * connects lazily, the first time the returned tool is actually executed, so a worker can
   * reconstruct an entire tool map at startup and only pay for connections to the servers whose
   * tools the model really calls.
   *
   * The returned tool behaves exactly like one from `listTools()`: same strict-mode metadata,
   * approval policy, structured content handling, in-band tool errors, progress metadata, abort
   * signal support, and reconnect/retry behavior.
   *
   * @param serverName Name of the server the definition came from, as configured on this client.
   * @param definition A definition previously obtained from {@link listToolDefinitions}.
   */
  public async toolFromDefinition({
    serverName,
    definition,
  }: {
    serverName: string;
    definition: SerializableMCPToolDefinition;
  }): Promise<Tool<any, any, any, any>> {
    this.addToInstanceCache();
    // getOrCreateClient constructs the client without connecting; connection is deferred to
    // the tool's first execution.
    const client = await this.getOrCreateClient(serverName, this.getServerConfig(serverName));
    return client.toolFromDefinition({ definition });
  }

  /**
   * Rebuilds an entire cached catalog into a namespaced tool map, without connecting.
   *
   * This is the cached counterpart to `listTools()`: it produces the same `serverName_toolName`
   * keys, so an agent's tool map can be reconstructed on a cold start from a catalog in Redis
   * and dropped straight into the agent, with connections opened lazily per tool call.
   *
   * Servers present in the catalog but no longer configured on this client are skipped, so a
   * stale cached manifest degrades gracefully instead of throwing.
   *
   * @example
   * ```typescript
   * const definitions = JSON.parse(await cache.get('mcp-tools'));
   * const agent = new Agent({ tools: await mcp.toolsFromDefinitions({ definitions }), ... });
   * ```
   */
  public async toolsFromDefinitions({
    definitions: catalog,
  }: {
    definitions: SerializableMCPToolCatalog;
  }): Promise<Record<string, Tool<any, any, any, any>>> {
    const configuredServers = new Set(Object.keys(this.serverConfigs));
    const tools: Record<string, Tool<any, any, any, any>> = {};

    for (const [serverName, definitions] of Object.entries(catalog)) {
      if (!configuredServers.has(serverName)) {
        this.logger.warn('Skipping cached MCP tool definitions for a server that is no longer configured', {
          serverName,
        });
        continue;
      }

      for (const [toolName, definition] of Object.entries(definitions)) {
        tools[`${serverName}_${toolName}`] = await this.toolFromDefinition({ serverName, definition });
      }
    }

    return tools;
  }

  private async listResourcesWithErrors(options?: MCPDiscoveryOptions): Promise<{
    resources: Record<string, Resource[]>;
    errors: Record<string, string>;
    errorDetails: Record<string, MCPDiscoveryErrorDetails>;
    durations?: Record<string, number>;
  }> {
    const { values, ...diagnostics } = await this.discoverValuesWithErrors(
      async serverName => (await this.getConnectedClientForServer(serverName)).resources.list(),
      { errorId: 'MCP_CLIENT_LIST_RESOURCES_FAILED', logMessage: 'Failed to list resources from server:' },
      options,
    );
    return { resources: values, ...diagnostics };
  }

  private async listResourceTemplatesWithErrors(options?: MCPDiscoveryOptions): Promise<{
    templates: Record<string, ResourceTemplateType[]>;
    errors: Record<string, string>;
    errorDetails: Record<string, MCPDiscoveryErrorDetails>;
    durations?: Record<string, number>;
  }> {
    const { values, ...diagnostics } = await this.discoverValuesWithErrors(
      async serverName => (await this.getConnectedClientForServer(serverName)).resources.templates(),
      {
        errorId: 'MCP_CLIENT_LIST_RESOURCE_TEMPLATES_FAILED',
        logMessage: 'Failed to list resource templates from server:',
      },
      options,
    );
    return { templates: values, ...diagnostics };
  }

  private async listPromptsWithErrors(options?: MCPDiscoveryOptions): Promise<{
    prompts: Record<string, Prompt[]>;
    errors: Record<string, string>;
    errorDetails: Record<string, MCPDiscoveryErrorDetails>;
    durations?: Record<string, number>;
  }> {
    const { values, ...diagnostics } = await this.discoverValuesWithErrors(
      async serverName => (await this.getConnectedClientForServer(serverName)).prompts.list(),
      { errorId: 'MCP_CLIENT_LIST_PROMPTS_FAILED', logMessage: 'Failed to list prompts from server:' },
      options,
    );
    return { prompts: values, ...diagnostics };
  }

  private async discoverValuesWithErrors<T>(
    operation: (serverName: string) => Promise<T>,
    onError: { errorId: Uppercase<string>; logMessage: string },
    options?: MCPDiscoveryOptions,
  ): Promise<{
    values: Record<string, T>;
    errors: Record<string, string>;
    errorDetails: Record<string, MCPDiscoveryErrorDetails>;
    durations?: Record<string, number>;
  }> {
    const values: Record<string, T> = {};
    const errors: Record<string, string> = {};
    const errorDetails: Record<string, MCPDiscoveryErrorDetails> = {};
    const durations: Record<string, number> = {};
    const settled = await this.discoverAcrossServers(operation, onError, options);

    for (const { serverName, value, error, duration } of settled) {
      durations[serverName] = duration;
      if (error !== undefined) {
        errors[serverName] = error.message;
        errorDetails[serverName] = error;
      } else {
        values[serverName] = value;
      }
    }

    const result = { values, errors, errorDetails };
    return options ? { ...result, durations } : result;
  }

  /**
   * Runs a per-server discovery `operation` against every configured server
   * concurrently, isolating and logging per-server failures. Results are
   * returned in configuration order (`Object.keys(serverConfigs)`) so callers
   * fold them back deterministically.
   *
   * Mirrors the parallel teardown in `disconnect()`: each server is bounded by
   * its own request timeout, so total time is the slowest single server rather
   * than the sum, and one slow or unresponsive server never stalls the others.
   * Backs `listTools`/`listToolsets`, `resources.list`/`templates`, and
   * `prompts.list`.
   */
  private async discoverAcrossServers<T>(
    operation: (serverName: string) => Promise<T>,
    onError: { errorId: Uppercase<string>; logMessage: string },
    options?: MCPDiscoveryOptions,
  ): Promise<Array<ServerDiscoveryResult<T>>> {
    const serverNames = Object.keys(this.serverConfigs);
    return Promise.all(
      serverNames.map(async (serverName): Promise<ServerDiscoveryResult<T>> => {
        const startedAt = performance.now();
        let timer: NodeJS.Timeout | undefined;

        try {
          const operationPromise = operation(serverName);
          const value =
            options?.perServerTimeoutMs === undefined
              ? await operationPromise
              : await Promise.race([
                  operationPromise,
                  new Promise<never>((_, reject) => {
                    timer = setTimeout(
                      () => reject(new Error(`Discovery timed out after ${options.perServerTimeoutMs}ms`)),
                      options.perServerTimeoutMs,
                    );
                    timer.unref?.();
                  }),
                ]);

          return { serverName, value, error: undefined, duration: performance.now() - startedAt };
        } catch (error) {
          const discoveryError = getMCPDiscoveryErrorDetails(error);

          try {
            const mastraError = new MastraError(
              {
                id: onError.errorId,
                domain: ErrorDomain.MCP,
                category: ErrorCategory.THIRD_PARTY,
                details: { serverName },
              },
              error,
            );
            this.logger.trackException(mastraError);
            this.logger.error(onError.logMessage, { error: mastraError.toString() });
          } catch {
            // Error inspection performed by telemetry must not make aggregate
            // discovery reject. Preserve the normalized message even when a
            // hostile third-party Error uses throwing getters or Proxy traps.
            const fallbackError = new MastraError({
              id: onError.errorId,
              domain: ErrorDomain.MCP,
              category: ErrorCategory.THIRD_PARTY,
              text: discoveryError.message,
              details: { serverName },
            });
            this.logger.trackException(fallbackError);
            this.logger.error(onError.logMessage, { error: fallbackError.toString() });
          }
          return {
            serverName,
            value: undefined,
            error: discoveryError,
            duration: performance.now() - startedAt,
          };
        } finally {
          clearTimeout(timer);
        }
      }),
    );
  }

  /**
   * Creates `MCPServerBase` proxy objects for each server connection in this
   * MCPClient. The returned record can be spread directly into Mastra's
   * `mcpServers` config so that external (non-Mastra) servers appear in Studio
   * alongside local MCPServer instances.
   *
   * @returns Record mapping server names to proxy instances
   *
   * @example
   * ```typescript
   * const mcp = new MCPClient({
   *   servers: {
   *     trailhead: { command: 'npx', args: ['trailhead-server'] },
   *   },
   * });
   *
   * const mastra = new Mastra({
   *   mcpServers: {
   *     ...mcp.toMCPServerProxies(),
   *   },
   * });
   * ```
   */
  public toMCPServerProxies(): Record<string, MCPServerBase> {
    const proxies: Record<string, MCPServerBase> = {};
    for (const serverName of Object.keys(this.serverConfigs)) {
      proxies[serverName] = new MCPClientServerProxy({ name: serverName, id: serverName }, () =>
        this.getConnectedClientForServer(serverName),
      );
    }
    return proxies;
  }

  /**
   * Gets the stderr stream of a connected stdio server.
   *
   * Only available for servers using stdio transport with `stderr: 'pipe'`.
   * Returns null if the server is not connected, not using stdio, or stderr is not piped.
   *
   * @param serverName - The name of the server
   * @returns The stderr stream, or null
   */
  public getServerStderr(serverName: string): Stream | null {
    const client = this.mcpClientsById.get(serverName);
    if (!client) return null;
    return client.stderr;
  }

  private getServerConfig(serverName: string): MastraMCPServerDefinition {
    const serverConfig = this.serverConfigs[serverName];
    if (!serverConfig) {
      throw new Error(`Server configuration not found for name: ${serverName}`);
    }
    return serverConfig;
  }

  private async getOrCreateClient(name: string, config: MastraMCPServerDefinition): Promise<InternalMastraMCPClient> {
    if (this.disconnectPromise) {
      await this.disconnectPromise;
    }

    const exists = this.mcpClientsById.has(name);
    const existingClient = this.mcpClientsById.get(name);

    this.logger.debug('Checking connected client', { name, exists });

    if (exists) {
      // This is just to satisfy Typescript since technically you could have this.mcpClientsById.set('someKey', undefined);
      // Should never reach this point basically we always create a new MastraMCPClient instance when we add to the Map.
      if (!existingClient) {
        throw new Error(`Client ${name} exists but is undefined`);
      }
      return existingClient;
    }

    const mcpClient = new InternalMastraMCPClient({
      name,
      server: config,
      timeout: config.timeout ?? this.defaultTimeout,
      clientInfo: {
        name: config.clientInfo?.name ?? this.defaultClientInfo?.name,
        version: config.clientInfo?.version ?? this.defaultClientInfo?.version,
      },
    });

    mcpClient.__setLogger(this.logger);

    this.mcpClientsById.set(name, mcpClient);

    return mcpClient;
  }

  private async getConnectedClient(name: string, config: MastraMCPServerDefinition): Promise<InternalMastraMCPClient> {
    this.logger.debug('Connecting to MCP server', { name });

    const mcpClient = await this.getOrCreateClient(name, config);

    try {
      await mcpClient.connect();
    } catch (e) {
      throw this.handleConnectError(name, e);
    }
    this.logger.debug('Connected to MCP server', { name });
    return mcpClient;
  }

  private handleConnectError(name: string, error: unknown): MastraError {
    const mastraError = new MastraError(
      {
        id: 'MCP_CLIENT_CONNECT_FAILED',
        domain: ErrorDomain.MCP,
        category: ErrorCategory.THIRD_PARTY,
        text: `Failed to connect to MCP server ${name}: ${
          error instanceof Error ? error.stack || error.message : String(error)
        }`,
        details: {
          name,
        },
      },
      error,
    );
    this.logger.trackException(mastraError);
    this.logger.error('MCPClient errored connecting to MCP server:', { error: mastraError.toString() });
    // Keep the client when authorization is required: it carries the needs-auth
    // state and the pending transport that authenticate() completes.
    if (!(error instanceof UnauthorizedError)) {
      this.mcpClientsById.delete(name);
    }
    return mastraError;
  }

  private async getConnectedClientForServer(serverName: string): Promise<InternalMastraMCPClient> {
    return this.getConnectedClient(serverName, this.getServerConfig(serverName));
  }

  private async getClientForServer(serverName: string): Promise<InternalMastraMCPClient> {
    return this.getOrCreateClient(serverName, this.getServerConfig(serverName));
  }

  private async getToolsForServer(serverName: string): Promise<Record<string, Tool<any, any, any, any>>> {
    for (let attempt = 1; attempt <= TOOL_DISCOVERY_MAX_ATTEMPTS; attempt++) {
      try {
        const client = await this.getConnectedClientForServer(serverName);
        return await client.tools();
      } catch (error) {
        if (attempt === TOOL_DISCOVERY_MAX_ATTEMPTS || !isReconnectableMCPError(error)) {
          throw error;
        }

        this.logger.debug('Retrying MCP tool discovery after reconnect', {
          serverName,
          attempt,
          maxAttempts: TOOL_DISCOVERY_MAX_ATTEMPTS,
          error: error instanceof Error ? error.message : String(error),
        });

        await this.reconnectServer(serverName);
      }
    }

    return {};
  }
}
